import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, X } from 'lucide-react';
import { useSubscriptionStatus } from '@/hooks/useBilling';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/enums';
import { SubscriptionStatus } from '@/types/billing';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';

const SESSION_KEY = 'billing_banner_dismissed';

const SubscriptionBanner: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: subscription, isLoading, error } = useSubscriptionStatus();

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === 'true',
  );

  if (!user || user.role === UserRole.ADMIN || user.role === UserRole.CUSTOMER) {
    return null;
  }

  if (isLoading || error || !subscription) {
    return null;
  }

  if (dismissed) {
    return null;
  }

  if (
    subscription.status === SubscriptionStatus.ACTIVE &&
    subscription.days_remaining > 3
  ) {
    return null;
  }

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setDismissed(true);
  };

  const handleNavigate = () => {
    navigate(ROUTES.BILLING);
  };

  const variant = getVariant(subscription.status, subscription.days_remaining);

  return (
    <div className={cn('w-full px-4 py-2.5', variant.bg)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {variant.icon}
          <span className={cn('text-sm font-medium', variant.text)}>
            {variant.message}
          </span>
          {variant.showButton && (
            <button
              onClick={handleNavigate}
              className={cn(
                'ml-2 shrink-0 rounded-md px-3 py-1 text-sm font-semibold transition-colors',
                variant.buttonClasses,
              )}
            >
              Оплатить
            </button>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className={cn(
            'shrink-0 rounded p-0.5 transition-colors',
            variant.dismissClasses,
          )}
          aria-label="Скрыть"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

function getVariant(
  status: SubscriptionStatus,
  daysRemaining: number,
): {
  bg: string;
  text: string;
  message: string;
  icon: React.ReactNode;
  showButton: boolean;
  buttonClasses: string;
  dismissClasses: string;
} {
  switch (status) {
    case SubscriptionStatus.TRIALING:
      return {
        bg: 'bg-indigo-50 border-b border-indigo-100',
        text: 'text-indigo-800',
        message: `Пробный период: осталось ${daysRemaining} дн.`,
        icon: <Clock className="h-4 w-4 shrink-0 text-indigo-600" />,
        showButton: true,
        buttonClasses:
          'bg-indigo-600 text-white hover:bg-indigo-700',
        dismissClasses: 'text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100',
      };

    case SubscriptionStatus.ACTIVE:
      return {
        bg: 'bg-amber-50 border-b border-amber-100',
        text: 'text-amber-800',
        message: `Подписка истекает через ${daysRemaining} дн.`,
        icon: <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />,
        showButton: false,
        buttonClasses: '',
        dismissClasses: 'text-amber-400 hover:text-amber-600 hover:bg-amber-100',
      };

    case SubscriptionStatus.PAST_DUE:
      return {
        bg: 'bg-orange-50 border-b border-orange-100',
        text: 'text-orange-800',
        message: `Проблема с оплатой. Осталось ${daysRemaining} дн.`,
        icon: <AlertTriangle className="h-4 w-4 shrink-0 text-orange-500" />,
        showButton: true,
        buttonClasses:
          'bg-orange-600 text-white hover:bg-orange-700',
        dismissClasses: 'text-orange-400 hover:text-orange-600 hover:bg-orange-100',
      };

    case SubscriptionStatus.EXPIRED:
      return {
        bg: 'bg-red-50 border-b border-red-100',
        text: 'text-red-800',
        message: 'Подписка истекла',
        icon: <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />,
        showButton: true,
        buttonClasses:
          'bg-red-600 text-white hover:bg-red-700',
        dismissClasses: 'text-red-400 hover:text-red-600 hover:bg-red-100',
      };

    default:
      return {
        bg: 'bg-gray-50 border-b border-gray-100',
        text: 'text-gray-700',
        message: '',
        icon: null,
        showButton: false,
        buttonClasses: '',
        dismissClasses: 'text-gray-400 hover:text-gray-600',
      };
  }
}

export default SubscriptionBanner;
